<script setup lang="ts">
  import { computed } from 'vue';
  import { changeChipLabel, type CorrectedWord } from '../model/fill-logs-text';
  import type { FillLogsResponseView } from '../model/fill-logs-timeline';

  const props = defineProps<{
    response: FillLogsResponseView;
    selected: boolean;
    first: boolean;
    last: boolean;
    correctedWord: CorrectedWord;
  }>();

  const emit = defineEmits<{
    (e: 'select', number: number): void;
  }>();

  const lineClass = computed(() => {
    if (props.first && props.last) return 'hidden';
    if (props.first) return 'top-7 bottom-0';
    if (props.last) return 'top-0 h-7';
    return 'inset-y-0';
  });

  const isErp = computed(() => props.response.kind === 'erp');
  const testId = computed(() => (isErp.value ? 'fill-logs-erp-entry' : `fill-logs-response-${props.response.number}`));

  const circleClass = computed(() => {
    if (props.response.correctedCount > 0) {
      return [
        'border-change-fixed-border bg-change-fixed text-blue-800',
        props.selected ? 'ring-2 ring-[var(--theme-secondary-ink)]' : '',
      ];
    }
    return props.selected
      ? 'border-[var(--theme-secondary-ink)] bg-white-01 text-[var(--theme-secondary-ink)]'
      : 'border-gray-02 bg-white-01 text-gray-03';
  });
</script>

<template>
  <li class="relative">
    <span
      class="absolute left-[31px] w-0.5 bg-gray-02"
      :class="lineClass"
      aria-hidden="true"
    />
    <button
      type="button"
      class="relative grid w-full grid-cols-[32px_minmax(0,1fr)] gap-3 px-4 py-3 text-left hover:bg-[var(--theme-secondary-tint)] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--theme-secondary-ink)]"
      :class="{ 'bg-[var(--theme-secondary-tint)]': selected }"
      :aria-current="selected ? 'true' : undefined"
      :data-number="response.number"
      :data-testid="testId"
      @click="emit('select', response.number)"
    >
      <span
        v-if="selected"
        class="absolute inset-y-0 left-0 w-[3px] bg-[var(--theme-secondary-ink)]"
        aria-hidden="true"
      />
      <span
        class="relative z-[1] flex size-8 items-center justify-center rounded-full border-2 text-[13px] font-semibold tabular-nums"
        :class="circleClass"
        data-testid="fill-logs-response-number"
      >
        <span
          v-if="isErp"
          class="text-[10px] tracking-[.04em]"
          aria-hidden="true"
        >ERP</span>
        <template v-else>{{ response.number }}</template>
      </span>
      <span class="flex min-w-0 flex-col gap-0.5">
        <span class="truncate text-sm font-medium text-gray-07">{{ response.authorName }}</span>
        <span class="text-xs text-gray-03 tabular-nums">
          {{ response.instant }}, {{ isErp ? 'dados do aparelho' : `etapa ${response.stageName}` }}
        </span>
        <span
          v-if="response.kind === 'pdf'"
          class="mt-1 flex flex-wrap gap-1"
        >
          <span
            class="rounded-full bg-gray-06 px-2 py-0.5 text-[11px]/3.5 font-medium text-gray-07"
            data-testid="fill-logs-chip-pdf"
          >
            PDF enviado
          </span>
        </span>
        <span
          v-else-if="response.filledCount > 0 || response.correctedCount > 0"
          class="mt-1 flex flex-wrap gap-1"
        >
          <span
            v-if="response.filledCount > 0"
            class="rounded-full bg-change-added px-2 py-0.5 text-[11px]/3.5 font-medium text-toast-success-icon"
            data-testid="fill-logs-chip-filled"
          >
            {{ changeChipLabel(response.filledCount, 'preenchido') }}
          </span>
          <span
            v-if="response.correctedCount > 0"
            class="rounded-full bg-change-fixed px-2 py-0.5 text-[11px]/3.5 font-medium text-blue-800"
            data-testid="fill-logs-chip-corrected"
          >
            {{ changeChipLabel(response.correctedCount, correctedWord) }}
          </span>
        </span>
      </span>
    </button>
  </li>
</template>
